import { useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { formatErr, http, imgUrl, TYPES } from "../../lib/api";

const EMPTY = { title: "", make: "", model: "", year_from: "", year_to: "", airbag_type: "driver", part_number: "", condition: "Used", price: "", stock: 1, description: "", images: [], featured: false };
const input = "h-11 w-full border border-white/10 bg-[#121820] px-3 text-sm outline-none focus:border-[#E11D48]";

const F = ({ label, children }) => (
  <label className="block space-y-1"><span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</span>{children}</label>
);

export const ProductForm = ({ product, onSaved }) => {
  const [f, setF] = useState(product ? { ...EMPTY, ...product, year_from: product.year_from ?? "", year_to: product.year_to ?? "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    setUploading(true);
    try {
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await http.post("/upload", fd);
        paths.push(data.path);
      }
      setF((prev) => ({ ...prev, images: [...prev.images, ...paths] }));
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, price: Number(f.price), stock: Number(f.stock), year_from: f.year_from ? Number(f.year_from) : null, year_to: f.year_to ? Number(f.year_to) : null };
    delete body.id;
    delete body.created_at;
    try {
      if (product?.id) await http.put(`/products/${product.id}`, body);
      else await http.post("/products", body);
      toast.success(product ? "Airbag updated" : "Airbag added");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="admin-product-form" className="space-y-4">
      <F label="Title"><input required data-testid="pf-title" value={f.title} onChange={set("title")} className={input} placeholder="BMW 3 Series F30 Driver Airbag" /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Make"><input required data-testid="pf-make" value={f.make} onChange={set("make")} className={input} placeholder="BMW" /></F>
        <F label="Model"><input data-testid="pf-model" value={f.model} onChange={set("model")} className={input} placeholder="3 Series" /></F>
        <F label="Year from"><input type="number" data-testid="pf-year-from" value={f.year_from} onChange={set("year_from")} className={input} /></F>
        <F label="Year to"><input type="number" data-testid="pf-year-to" value={f.year_to} onChange={set("year_to")} className={input} /></F>
        <F label="Type">
          <select data-testid="pf-type" value={f.airbag_type} onChange={set("airbag_type")} className={input}>
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </F>
        <F label="Condition">
          <select data-testid="pf-condition" value={f.condition} onChange={set("condition")} className={input}>
            <option>Used</option><option>New</option><option>Refurbished</option>
          </select>
        </F>
        <F label="Price £"><input required type="number" step="0.01" min="0" data-testid="pf-price" value={f.price} onChange={set("price")} className={input} /></F>
        <F label="Stock"><input required type="number" min="0" data-testid="pf-stock" value={f.stock} onChange={set("stock")} className={input} /></F>
      </div>
      <F label="Part number"><input data-testid="pf-part-number" value={f.part_number} onChange={set("part_number")} className={input} /></F>
      <F label="Description"><textarea rows={3} data-testid="pf-description" value={f.description} onChange={set("description")} className={`${input} h-auto py-2`} /></F>
      <div>
        <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Photos</span>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {f.images.map((im, i) => (
            <div key={im + i} className="relative aspect-square">
              <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
              <button type="button" data-testid={`pf-remove-image-${i}`} onClick={() => setF({ ...f, images: f.images.filter((_, j) => j !== i) })} className="absolute right-1 top-1 rounded-full bg-black/70 p-1"><X className="h-3 w-3" /></button>
            </div>
          ))}
          <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-white/20 text-xs text-slate-300 hover:border-[#E11D48]">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            Upload
            <input type="file" accept="image/*" multiple className="hidden" data-testid="pf-image-upload" onChange={upload} />
          </label>
        </div>
      </div>
      <label className="flex items-center gap-3 text-sm">
        <Switch data-testid="pf-featured" checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} /> Show on home page
      </label>
      <button data-testid="pf-save-button" disabled={busy || uploading} className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] font-semibold hover:bg-[#F43F5E] disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {product ? "Save changes" : "Add airbag"}
      </button>
    </form>
  );
};
